import React, { FormEvent, useCallback, useEffect, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Stack,
  Table,
} from "react-bootstrap";

import {
  createPlatformUser,
  listPlatformUsers,
  PlatformUserRecord,
  revokePlatformUser,
} from "../../api/platformUsers";

interface PlatformUsersPageProps {
  user: User;
}

interface CreateFormState {
  email: string;
  password: string;
  displayName: string;
}

const EMPTY_FORM: CreateFormState = {
  email: "",
  password: "",
  displayName: "",
};

const formatTimestamp = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PlatformUsersPage: React.FC<PlatformUsersPageProps> = ({ user }) => {
  const [records, setRecords] = useState<PlatformUserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [revokingUid, setRevokingUid] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listPlatformUsers(user);
      setRecords(data);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Unable to load platform users.",
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleChange = (field: keyof CreateFormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitMessage(null);

    const email = form.email.trim();
    if (!email) {
      setSubmitError("Email is required.");
      return;
    }

    const password = form.password.trim();
    const existing = records.find(
      (record) => (record.email ?? "").toLowerCase() === email.toLowerCase(),
    );
    if (!existing && password.length < 8) {
      setSubmitError(
        "Password is required when creating a new user and must be at least 8 characters.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const created = await createPlatformUser(user, {
        email,
        password: password || undefined,
        displayName: form.displayName || undefined,
      });
      setSubmitMessage(
        existing
          ? `Updated ${created.email} (god role refreshed).`
          : `Created ${created.email} as a platform god user.`,
      );
      setForm(EMPTY_FORM);
      await reload();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Unable to save the user.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (record: PlatformUserRecord) => {
    if (record.uid === user.uid) {
      setRevokeError("You cannot revoke your own god role.");
      return;
    }
    const confirmed = window.confirm(
      `Revoke platform god role from ${record.email ?? record.uid}?\n\n` +
        "The Firebase Auth account will remain, but platform panel access " +
        "and god claims will be removed.",
    );
    if (!confirmed) return;

    setRevokeError(null);
    setRevokingUid(record.uid);
    try {
      await revokePlatformUser(user, record.uid);
      await reload();
    } catch (error) {
      setRevokeError(
        error instanceof Error ? error.message : "Unable to revoke access.",
      );
    } finally {
      setRevokingUid(null);
    }
  };

  return (
    <>
      <header className="content__header mb-4">
        <div>
          <h1 className="page-header__title">
            Platform users
          </h1>
          <p className="page-header__lead">
            Manage accounts with the platform panel <code>god</code> role.
            These users have universal access across every organization.
          </p>
        </div>
      </header>

      <Row xs={1} lg={2} className="g-4">
        <Col>
          <Card className="shadow-sm h-100">
            <Card.Header className="border-0">
              <Card.Title className="mb-0">Create or update</Card.Title>
              <Card.Subtitle className="text-muted">
                Provisions Firebase Auth claims + the mirrored Firestore user
                doc.
              </Card.Subtitle>
            </Card.Header>
            <Card.Body>
              {submitMessage ? (
                <Alert variant="success" onClose={() => setSubmitMessage(null)} dismissible>
                  {submitMessage}
                </Alert>
              ) : null}
              {submitError ? (
                <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                  {submitError}
                </Alert>
              ) : null}
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="platformUserEmail" className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="ops@mayorx.net"
                    autoComplete="off"
                    required
                  />
                </Form.Group>
                <Form.Group controlId="platformUserDisplayName" className="mb-3">
                  <Form.Label>Display name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.displayName}
                    onChange={handleChange("displayName")}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                </Form.Group>
                <Form.Group controlId="platformUserPassword" className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={form.password}
                    onChange={handleChange("password")}
                    placeholder="Required for new users (min 8 chars)"
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <Form.Text muted>
                    Leave blank to keep the existing password on an already-registered email.
                  </Form.Text>
                </Form.Group>
                <Stack direction="horizontal" gap={2} className="justify-content-end">
                  <Button
                    variant="outline-secondary"
                    type="button"
                    disabled={submitting}
                    onClick={() => setForm(EMPTY_FORM)}
                  >
                    Reset
                  </Button>
                  <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? "Saving..." : "Save god user"}
                  </Button>
                </Stack>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col>
          <Card className="shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center border-0">
              <div>
                <Card.Title className="mb-0">Existing god users</Card.Title>
                <Card.Subtitle className="text-muted">
                  {records.length} account{records.length === 1 ? "" : "s"}
                </Card.Subtitle>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => void reload()}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </Card.Header>
            <Card.Body>
              {loadError ? (
                <Alert variant="danger">{loadError}</Alert>
              ) : null}
              {revokeError ? (
                <Alert
                  variant="danger"
                  onClose={() => setRevokeError(null)}
                  dismissible
                >
                  {revokeError}
                </Alert>
              ) : null}
              {loading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  <span>Loading...</span>
                </div>
              ) : records.length === 0 ? (
                <div className="text-muted small">
                  No god users yet. Create the first one using the form.
                </div>
              ) : (
                <Table
                  responsive
                  size="sm"
                  className="mb-0 align-middle text-nowrap"
                >
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Last sign-in</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => {
                      const isSelf = record.uid === user.uid;
                      const isRevoking = revokingUid === record.uid;
                      return (
                        <tr key={record.uid}>
                          <td>
                            <div className="fw-semibold">
                              {record.email ?? "—"}
                              {isSelf ? (
                                <Badge bg="info" className="ms-2">
                                  You
                                </Badge>
                              ) : null}
                            </div>
                            <div className="text-muted small">
                              {record.displayName ?? record.uid}
                            </div>
                          </td>
                          <td>
                            {record.disabled ? (
                              <Badge bg="secondary">Disabled</Badge>
                            ) : (
                              <Badge bg="success">Active</Badge>
                            )}
                          </td>
                          <td className="text-muted small">
                            {formatTimestamp(record.createdAt)}
                          </td>
                          <td className="text-muted small">
                            {formatTimestamp(record.lastSignInAt)}
                          </td>
                          <td className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={isSelf || isRevoking}
                              onClick={() => void handleRevoke(record)}
                            >
                              {isRevoking ? "Revoking..." : "Revoke"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PlatformUsersPage;
